import { useNavigate } from 'react-router';
import type { FolderResponseDto } from '@inference.engine/workspace-api-client/web';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@monksflow/monks-ui';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { SETTLED_EXIT_SECONDS } from '@/shared/ui/animations';
import { FolderIcon, MessageSquareIcon } from 'lucide-react';
import { AssetThumbnail, type ThumbnailRenderable } from '@/entities/project-file';
import { projectFoldersQuery } from '@/entities/project-folder';
import { ROUTES } from '@/shared/config/routes';
import { formatShortDate } from '@/shared/utils/format';
import { RAIL_PAGE_SIZE } from '../ApprovalFolderRail';
import { ApprovalProgressBadge } from '../ApprovalProgressBadge';
import { useTranslation } from 'react-i18next';

interface SubfolderGridProps {
  projectId: string;
  parentId: string;
}

const SKELETON_CARDS = [0, 1, 2, 3];

const SUBFOLDER_GRID_CLASS =
  'grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] content-start gap-app-inset';

/**
 * What a folder that holds other folders shows instead of assets.
 *
 * The same page size as the rail, so opening a branch there and opening the
 * folder itself are served from one cache entry.
 */
export function SubfolderGrid(props: SubfolderGridProps) {
  const { t } = useTranslation('projects');
  const navigate = useNavigate();
  const shouldReduceMotion = useReducedMotion();
  const { data, isLoading } = useQuery(
    projectFoldersQuery(props.projectId, {
      parentId: props.parentId,
      page: 1,
      pageSize: RAIL_PAGE_SIZE,
    }),
  );

  // One page deep, like the rail. A parent past that would lose the folders
  // below the cut with nothing to say it had, so the count that did not fit is
  // stated rather than dropped.
  const subfolders = data?.items ?? [];
  const unlistedCount = (data?.total ?? 0) - subfolders.length;

  // Matches the asset grid: the cards are already in place underneath while
  // the placeholder over them dissolves, so only the leaving half animates.
  return (
    <div className="grid overflow-y-auto" data-testid="subfolder-grid">
      <AnimatePresence initial={false}>
        {isLoading ? (
          <motion.div
            key="loading"
            className={`col-start-1 row-start-1 ${SUBFOLDER_GRID_CLASS}`}
            exit={{ opacity: 0 }}
            transition={{ duration: shouldReduceMotion ? 0 : SETTLED_EXIT_SECONDS }}
          >
            {SKELETON_CARDS.map((card) => (
              <Skeleton
                key={card}
                data-testid="skeleton-loader"
                className="h-[148px] w-full rounded-xl"
              />
            ))}
          </motion.div>
        ) : (
          <div key="rows" className={`col-start-1 row-start-1 ${SUBFOLDER_GRID_CLASS}`}>
            {subfolders.map((folder) => (
              <SubfolderCard
                key={folder.id}
                folder={folder}
                onOpen={() =>
                  void navigate(ROUTES.PROJECT_FOLDER_DETAIL(props.projectId, folder.id))
                }
              />
            ))}
          </div>
        )}
      </AnimatePresence>

      {unlistedCount > 0 && (
        <p className="pt-md text-body-sm text-muted-foreground">
          {t('projectDetail.subfolderGrid.foldersNotShown', { count: unlistedCount })}
        </p>
      )}
    </div>
  );
}

function SubfolderCard({ folder, onOpen }: { folder: FolderResponseDto; onOpen: () => void }) {
  const { t } = useTranslation('projects');

  // `FolderThumbnailDto` already satisfies the thumbnail contract, so the
  // resolved asset renders here with no stub fields. The server seeds it from
  // the oldest upload in the subtree, so a folder shows what is in it rather
  // than a generic icon.
  const { thumbnail } = folder;
  const thumbnailFile: ThumbnailRenderable | null = thumbnail
    ? {
        fileName: thumbnail.fileName,
        fileType: thumbnail.fileType,
        assetType: thumbnail.assetType,
        downloadUrl: thumbnail.downloadUrl,
      }
    : null;

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`subfolder-card-${folder.id}`}
      className="flex h-[148px] flex-col gap-md rounded-xl border border-border p-lg text-left transition-[border-color] duration-fast ease-out hover:border-border-strong"
    >
      <div className="flex items-center justify-between gap-sm">
        <span className="flex size-3xl shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted">
          {thumbnailFile ? (
            <AssetThumbnail file={thumbnailFile} className="size-full" iconSize="size-[18px]" />
          ) : (
            <FolderIcon className="size-[18px] text-foreground" />
          )}
        </span>

        <div className="flex items-center gap-xs">
          {folder.commentsCount > 0 && (
            <span className="flex items-center gap-2xs text-body-sm font-medium text-blue-600">
              <MessageSquareIcon className="size-4" />
              {folder.commentsCount}
            </span>
          )}
          <ApprovalProgressBadge progress={folder.approvalProgress} />
        </div>
      </div>

      <div className="flex min-w-0 flex-col gap-3xs">
        <span className="truncate text-body-lg font-medium text-foreground">{folder.name}</span>
        <span className="truncate text-body-md text-muted-foreground">
          {/* The name is resolved server-side and omitted when the directory
              lookup fails, so the line falls back to the date rather than
              holding a gap open for a name that is not coming. */}
          {folder.creator
            ? t('projectDetail.subfolderGrid.createdByOn', {
                name: folder.creator.name,
                date: formatShortDate(folder.createdAt),
              })
            : t('projectDetail.subfolderGrid.created', { date: formatShortDate(folder.createdAt) })}
        </span>
      </div>
    </button>
  );
}
